/*
 * Collection Sidebar Search
 *
 * Client-side instant search scoped to the current collection.
 * Fetches /collections/{handle}/products.json (up to 1000 products)
 * and filters them in-memory by title, vendor, SKU and tags.
 */

(function () {
  if (window.__collectionSidebarSearchLoaded) return;
  window.__collectionSidebarSearchLoaded = true;

  // Helpful boot log for debugging. Safe to remove.
  // eslint-disable-next-line no-console
  console.log('[collection-sidebar-search] script loaded');

  class CollectionSidebarSearch extends HTMLElement {
    constructor() {
      super();
      this.handle = this.dataset.collectionHandle;
      this.currency = this.dataset.currencySymbol || '';
      this.input = this.querySelector('input[name="q"]');
      this.form = this.querySelector('form');
      this.resultsBox = this.querySelector('[data-results]');
      this.clearBtn = this.querySelector('[data-action="clear"]');
      this.products = null;
      this.loading = null;
      this.debounceTimer = null;
      this.pageSize = 8;
      this.displayLimit = this.pageSize;
      this.lastMatches = [];
      this.lastQuery = '';
    }

    connectedCallback() {
      if (!this.input || !this.handle) {
        console.warn('[collection-sidebar-search] missing input or handle', this);
        return;
      }

      console.log('[collection-sidebar-search] bound to collection:', this.handle);

      this.input.addEventListener('input', this._onInput.bind(this));
      this.input.addEventListener('focus', this._onInput.bind(this));
      this.form.addEventListener('submit', this._onSubmit.bind(this));
      this.addEventListener('click', this._onResultClick.bind(this));

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', this._onClear.bind(this));
      }

      document.addEventListener('click', (e) => {
        const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
        if (path.indexOf(this) !== -1) return;
        if (this.contains(e.target)) return;
        this._close();
      });

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this._close();
          this.input.blur();
        }
      });
    }

    _onInput() {
      const hasValue = (this.input.value || '').length > 0;
      if (this.clearBtn) this.clearBtn.hidden = !hasValue;

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this._runQuery(), 120);
    }

    _onSubmit(e) {
      e.preventDefault();
      this._runQuery();
    }

    _onClear(e) {
      e.preventDefault();
      this.input.value = '';
      this.input.focus();
      this.lastMatches = [];
      this.lastQuery = '';
      this.displayLimit = this.pageSize;
      if (this.clearBtn) this.clearBtn.hidden = true;
      this._close();
    }

    _onResultClick(e) {
      const loadMore = e.target.closest('[data-load-more]');
      if (loadMore) {
        e.preventDefault();
        e.stopPropagation();
        this.displayLimit += this.pageSize;
        this._renderMatches();
        this.input.focus({ preventScroll: true });
        return;
      }

      const link = e.target.closest('a[data-result]');
      if (!link) return;
      this._close();
    }

    async _loadProducts() {
      if (this.products) return this.products;
      if (this.loading) return this.loading;

      this.loading = (async () => {
        const all = [];
        for (let page = 1; page <= 4; page++) {
          try {
            const url =
              '/collections/' +
              encodeURIComponent(this.handle) +
              '/products.json?limit=250&page=' +
              page;
            const res = await fetch(url, { credentials: 'same-origin' });
            if (!res.ok) {
              console.warn(
                '[collection-sidebar-search] fetch returned',
                res.status,
                'for',
                url
              );
              break;
            }
            const data = await res.json();
            const chunk = (data && data.products) || [];
            if (chunk.length === 0) break;
            all.push.apply(all, chunk);
            if (chunk.length < 250) break;
          } catch (err) {
            console.warn('[collection-sidebar-search] fetch failed', err);
            break;
          }
        }
        console.log(
          '[collection-sidebar-search] loaded',
          all.length,
          'products from collection',
          this.handle
        );
        this.products = all;
        return all;
      })();

      return this.loading;
    }

    _normalize(str) {
      if (!str) return '';
      return String(str)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    }

    async _runQuery() {
      const raw = (this.input.value || '').trim();
      const query = this._normalize(raw);

      if (!query) {
        this._close();
        return;
      }

      this._showLoading();

      const products = await this._loadProducts();
      if (!products) return;

      const terms = query.split(/\s+/).filter(Boolean);

      const matches = products.filter((p) => {
        const haystack = [
          p.title,
          p.vendor,
          p.product_type,
          (p.tags || []).join(' '),
          (p.variants || []).map((v) => v.sku).join(' '),
          (p.variants || []).map((v) => v.title).join(' '),
        ].join(' ');
        const normalized = this._normalize(haystack);
        return terms.every((t) => normalized.indexOf(t) !== -1);
      });

      console.log(
        '[collection-sidebar-search] query:',
        raw,
        '->',
        matches.length,
        'matches'
      );

      const isNewQuery = raw !== this.lastQuery;
      this.lastQuery = raw;
      this.lastMatches = matches;
      if (isNewQuery) this.displayLimit = this.pageSize;

      this._renderMatches();
    }

    _showLoading() {
      if (!this.resultsBox) return;
      this.resultsBox.innerHTML =
        '<div class="collection-sidebar-search__status">…</div>';
      this._open();
    }

    _renderMatches() {
      if (!this.resultsBox) return;

      const matches = this.lastMatches || [];

      if (matches.length === 0) {
        this.resultsBox.innerHTML =
          '<div class="collection-sidebar-search__status">' +
          this._escape(this._noResultsLabel(this.lastQuery)) +
          '</div>';
        this._open();
        return;
      }

      const limit = Math.min(this.displayLimit, matches.length);
      const limited = matches.slice(0, limit);
      const remaining = matches.length - limited.length;
      const items = limited.map((p) => this._renderItem(p)).join('');

      let footer = '';
      if (remaining > 0) {
        const loadMoreLabel = this.dataset.labelLoadMore || 'Load more';
        const count = Math.min(remaining, this.pageSize);
        footer =
          '<button type="button" class="collection-sidebar-search__load-more" data-load-more>' +
          '<span>' +
          this._escape(loadMoreLabel) +
          '</span>' +
          '<span class="collection-sidebar-search__load-more-count">+' +
          count +
          '</span>' +
          '</button>';
      }

      this.resultsBox.innerHTML = items + footer;
      this._open();
    }

    _renderItem(p) {
      const image = this._imageUrl(p);
      const price = this._price(p);
      const url = '/products/' + encodeURIComponent(p.handle);

      return (
        '<a href="' +
        url +
        '" data-result class="collection-sidebar-search__result">' +
        (image
          ? '<img src="' +
            image +
            '" width="44" height="44" loading="lazy" alt="" class="collection-sidebar-search__result-image">'
          : '<span class="collection-sidebar-search__result-image collection-sidebar-search__result-image--empty"></span>') +
        '<span class="collection-sidebar-search__result-info">' +
        '<span class="collection-sidebar-search__result-title">' +
        this._escape(p.title) +
        '</span>' +
        (price
          ? '<span class="collection-sidebar-search__result-price">' +
            this._escape(price) +
            '</span>'
          : '') +
        '</span>' +
        '</a>'
      );
    }

    _imageUrl(p) {
      const img = p.images && p.images[0] ? p.images[0] : null;
      if (!img) return '';
      const src = typeof img === 'string' ? img : img.src || '';
      if (!src) return '';
      return src.replace(/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i, '_100x100.$1$2');
    }

    _price(p) {
      const variant = (p.variants && p.variants[0]) || null;
      if (!variant || variant.price == null) return '';
      const amount = parseFloat(variant.price);
      if (isNaN(amount)) return '';
      const formatted = amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return (this.currency || '') + formatted;
    }

    _noResultsLabel(term) {
      const tpl = this.dataset.labelNoResults || 'No results for "TERMS"';
      return tpl.replace('TERMS', term);
    }

    _escape(str) {
      const div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }

    _open() {
      if (!this.resultsBox) return;
      this.resultsBox.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    _close() {
      if (!this.resultsBox) return;
      this.resultsBox.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  if (!customElements.get('collection-sidebar-search')) {
    customElements.define('collection-sidebar-search', CollectionSidebarSearch);
  }
})();
